import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Badge } from "@ui/badge/Badge";
import { HBox, PanelBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import { IconArrowDown } from "@ui/icons/IconArrowDown";
import { IconMastercard } from "@ui/icons/IconMastercard";
import { IconVisa } from "@ui/icons/IconVisa";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import Signal from "badmfck-signal";
import { Cable, Link2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import { ITerminal } from "src/types/types";
import { TerminalBadge } from "../conponents/TerminalBadge";
import { TFeesTargetType } from "../Fees";
import { FormParser } from "../FormParser";
import { NoRecords } from "../providers/NoRecords";
import { AdminButton } from "../pages/administration/AdminControls";
import { channelStatus, compareText, matchesQuery, methodTypeLabel, uniqueSorted } from "../providers/providersData";
import {
	Card,
	CardBrandIcons,
	Chip,
	DialogBody,
	embeddedFormSx,
	EmptyState,
	FilterSelect,
	Item,
	ItemList,
	MethodTile,
	Mono,
	Notice,
	Pill,
	ProviderDialog,
	SearchField,
	Section,
	SkeletonRows,
	StatusPill,
	WarningText,
} from "../providers/providersUi";
import type { TPaymentProvider } from "../providers/ProvidersList";
import {
	editMerchantChannel,
	getChannelSignature,
	getChannelsForMerchant,
	getChannelsForProviderMethod,
	isSamePaymentChannel,
	mergePaymentChannel,
	S_CHANNEL_ITEM_POPUP,
	S_CHANNELS_LIST_REQUEST_UPDATE,
	type TPaymentChannel,
} from "./channelData";

export { S_CHANNEL_ITEM_POPUP, S_CHANNELS_LIST_REQUEST_UPDATE } from "./channelData";
export type { TPaymentChannel } from "./channelData";

export const ChannelsList = ({
	asProvider,
	asMerchant,
	onClick,
	expandChannelId,
}: {
	asMerchant?: { merchant_id?: number; method_id?: number };
	asProvider?: { method?: ITerminal; provider?: TPaymentProvider };
	splitByMethods?: boolean;
	onClick?: (channel: TPaymentChannel) => void;
	expandChannelId?: number;
}) => {
	useLocale();
	let request = getChannelsForProviderMethod(undefined, undefined);
	if (asProvider) request = getChannelsForProviderMethod(asProvider.provider, asProvider.method);
	if (asMerchant) request = getChannelsForMerchant(asMerchant.merchant_id, asMerchant.method_id);

	const [data, reload, busy] = useAPI<TPaymentChannel[]>(request);
	const popupChannel = S_CHANNEL_ITEM_POPUP.use();
	const noData = data?.data && data.data.length === 0 && busy === false;

	S_CHANNELS_LIST_REQUEST_UPDATE.use([reload], () => { reload(); });
	GD.S_TERMINALS_UPDATE.use([reload], () => { reload(); });

	useEffect(() => {
		if (!popupChannel || !data?.data) return;

		const freshChannel = data.data.find(channel => isSamePaymentChannel(channel, popupChannel));
		if (!freshChannel) return;

		const nextChannel = mergePaymentChannel(popupChannel, freshChannel, "server");
		if (getChannelSignature(nextChannel) === getChannelSignature(popupChannel)) return;

		S_CHANNEL_ITEM_POPUP.invoke(nextChannel);
	}, [data?.data, popupChannel]);

	return (
		<>
			<Error error={data?.error} />
			<ProgressBar busy={busy} />
			{noData && <NoRecords line1={msg("ui_d52700fe4e")} line2={msg("ui_744b1ace7f")} />}
			<VBox gap="tiny">
				{data?.data?.map((channel: TPaymentChannel, index: number) => {
					return (
						<ChannelItem
							key={channel.id + "_" + index}
							channel={channel}
							preview={!!onClick}
							onClick={onClick}
							initialExpanded={expandChannelId === channel.linked?.id}
						/>
					);
				})}
			</VBox>
			<Spacer variant="tiny" />

			{/** ADD CHANNEL BUTTON FOR MERCHANT */}
			{asMerchant && asMerchant.merchant_id && (
				<HBox>
					<Spacer grow /> <Button title={msg("addChannel")} onClick={() => S_CHANNELS_LIST_POPUP.invoke({ merchant_id: asMerchant.merchant_id })}></Button>
				</HBox>
			)}
		</>
	);
};

const ChannelItem = ({
	channel,
	preview,
	onClick,
	initialExpanded,
}: {
	channel: TPaymentChannel;
	preview?: boolean;
	onClick?: (channel: TPaymentChannel) => void;
	initialExpanded?: boolean;
}) => {
	useLocale();
	const [expanded] = useState(initialExpanded ?? false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (initialExpanded && ref.current) {
			ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, []);

	let status = channel.status;
	let name = channel.name;
	let id = channel.id;

	if (channel.linked) {
		if (status === "active") status = channel.linked.status;
		if (channel.linked.status === "deleted") status = "deleted";
		name = channel.name + (channel.linked.alias ? " - " + channel.linked.alias : "");
		id = channel.linked.id;
	}

	let click = undefined;
	if (onClick) click = () => onClick(channel);
	if (!onClick && !preview)
		click = () => {
			S_CHANNEL_ITEM_POPUP.invoke(channel);
		};

	return (
		<>
			<PanelBox ref={ref} variant="strict" onClick={click}>
				<HBox variant="left-center">
					<TerminalBadge terminal={channel.terminal} padding={0} size={18} simple />
					<Typo variant="medium" bold>
						{name}
					</Typo>
					<Typo>{id}</Typo>

					<InstrumentIcon icon={channel.instrument_type} />
					<Badge variant="outline" label={msg("templateCurrency_986d8c66", { v0: channel.currency })} />
					<Badge variant="outline" label={msg("templateStatus_c1c65bf0", { v0: status })} />
					<Spacer grow />
					{!preview && <IconArrowDown rotated={expanded} />}
				</HBox>
			</PanelBox>
		</>
	);
};

const InstrumentIcon = ({ icon }: { icon: string }) => {
	useLocale();
	if (icon === "visa") return <IconVisa />;
	if (icon === "mastercard") return <IconMastercard />;
	if (icon === "all")
		return (
			<HBox>
				<IconVisa />
				<IconMastercard />
			</HBox>
		);
	return <></>;
};

export const S_PAYMENT_CHANNEL_CREATE = new Signal<{
	id: number;
	payment_provider_id: number;
	provider_name?: string;
	onClose?: () => void;
	onSuccess?: () => void;
} | null>("S_PAYMENT_CHANNEL_CREATE");

export const PaymentChannelEditor = () => {
	useLocale();
	const signal = S_PAYMENT_CHANNEL_CREATE.use();

	if (!signal) return null;

	const close = () => {
		S_PAYMENT_CHANNEL_CREATE.invoke(null);
		if (signal.onClose) signal.onClose();
	};

	return (
		<ProviderDialog
			width={640}
			eyebrow={signal.provider_name ? msg("templatePaymentChannel_1b0e6869", { v0: signal.provider_name }) : msg("paymentChannel")}
			title={signal.id > 0 ? msg("editChannel") : msg("newChannel")}
			onClose={close}>
			<DialogBody>
				<FormParser
					appearance="modern"
					sx={embeddedFormSx}
					size="small"
					secondaryButton={null}
					onSubmit={async (data: unknown): Promise<any> => {
						const response = await GD.REQ_API.request({
							method: "payments/channel/set",
							data: data,
						});
						if (response.error) return response;
						S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
						signal.onSuccess?.();
						close();
						return data;
					}}
					onRequest={async (): Promise<any> => {
						const response = await GD.REQ_API.request({
							method: "payments/channel/get",
							data: {
								id: signal.id,
								provider_id: signal.payment_provider_id,
							},
						});
						if (response.error) return response.error;
						if (response.data.channels && response.data.channels.length) return response.data.channels[0];
						if (response.data) return response.data;
						return { code: -1, message: msg("channelNotFound") };
					}}
				/>
			</DialogBody>
		</ProviderDialog>
	);
};

export const S_MERCHANT_CHANNEL_EDIT = new Signal<{ channel: TPaymentChannel; merchant_id?: number; merchant_name?: string } | null>("S_MERCHANT_CHANNEL_EDIT");

const ChannelSummary = ({ channel }: { channel: TPaymentChannel }) => { useLocale(); return ((
	<Card>
		<ItemList>
			<Item>
				<MethodTile type={channel.terminal?.type} title={methodTypeLabel(channel.terminal?.type)} />
				<div className="item-main">
					<div style={{ color: "#182230", fontWeight: 600 }}>{channel.name}</div>
					<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px 8px", color: "#667085", fontSize: 12 }}>
						<span>{channel.provider?.name ?? "—"}</span>
						<span>· {channel.terminal?.name ?? methodTypeLabel(channel.terminal?.type)}</span>
						<Mono>#{channel.id}</Mono>
					</div>
				</div>
				<span className="item-side">
					<CardBrandIcons instrument={channel.instrument_type} />
					{channel.currency && <Chip>{channel.currency}</Chip>}
				</span>
			</Item>
		</ItemList>
	</Card>
)); };

export const MerchantChannelEditor = () => {
	useLocale();
	const signalResult = S_MERCHANT_CHANNEL_EDIT.use();
	if (!signalResult) return null;
	if (!signalResult.merchant_id && signalResult.channel.linked?.merchant_id) signalResult.merchant_id = signalResult.channel.linked.merchant_id;

	const { channel, merchant_id, merchant_name } = signalResult;

	// add channel only to merchant, for provider channels are added via provider edit page
	const addChannel = async (channel: TPaymentChannel, data: { alias: string; processing_mid: string }) => {
		const response = await GD.REQ_API.request({
			method: "payments/channel/connect",
			data: {
				channel_id: channel.id,
				merchant_id: merchant_id,

				mid: data.processing_mid,
				alias: data.alias,
			},
		});
		return response;
	};

	const close = () => {
		S_MERCHANT_CHANNEL_EDIT.invoke(null);
		S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
	};

	return (
		<ProviderDialog
			width={640}
			eyebrow={merchant_name ? msg("templateWebsite_e4b54006", { v0: merchant_name }) : msg("templateMerchant_bf3dbf70", { v0: merchant_id })}
			title={channel.linked ? msg("editLinkedChannel") : msg("linkChannel")}
			onClose={close}>
			<DialogBody>
				<Section title={msg("channel")} />
				<ChannelSummary channel={channel} />
				<Section title={channel.linked ? msg("changeChannelSetup") : msg("prepareChannelSetup")} />
				<p style={{ margin: "-2px 0 14px", color: "#667085", fontSize: 12 }}>{msg("setupChannelAliasAndMidForProcessingIfAvailable")}</p>
				<FormParser
					appearance="modern"
					sx={embeddedFormSx}
					size="small"
					grow
					secondaryButton={{
						title: msg("cancel"),
						onClick: close,
					}}
					primaryButton={{
						title: msg("save"),
						variant: "primarySmall",
					}}
					onSubmit={async (data: any): Promise<any> => {
						const alias = data.alias;
						const mid = data.mid;

						if (channel.linked && channel.linked.id) {
							const prepared = {
								linked_id: channel.linked.id,
								order: channel.linked.order,
								alias: alias,
								mid: mid,
							};
							const result = await editMerchantChannel(prepared);
							if (result.error) return result;
							S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
						} else {
							// ADD CHANNEL TO MERCHANT
							const prepared = {
								alias: alias,
								processing_mid: mid,
							};
							const result = await addChannel(channel, prepared);
							if (result.error) return result;
							S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
						}

						close();
						return data;
					}}
					onRequest={async (): Promise<any> => {
						return {
							alias: channel.linked?.alias ?? "",
							mid: channel.linked?.processing_mid ?? "",
						};
					}}
				/>
			</DialogBody>
		</ProviderDialog>
	);
};

// CHANNELS LIST
export const S_CHANNELS_LIST_POPUP = new Signal<{ merchant_id?: number; method_id?: number; merchant_name?: string } | null>("S_CHANNEL_LIST_POPUP");
export const ChannelsListPopup = () => {
	useLocale();
	const request = S_CHANNELS_LIST_POPUP.use();
	if (!request) return null;
	return <LinkChannelDialog key={request.merchant_id ?? 0} request={request} />;
};

/** Every provider channel, with the ones the merchant already uses marked; linking asks for the alias and MID. */
const LinkChannelDialog = ({ request }: { request: { merchant_id?: number; merchant_name?: string } }) => {
	useLocale();
	const [all] = useAPI<TPaymentChannel[]>(getChannelsForProviderMethod(undefined, undefined));
	const [linked, reloadLinked] = useAPI<TPaymentChannel[]>(request.merchant_id ? getChannelsForMerchant(request.merchant_id) : null);
	const [query, setQuery] = useState("");
	const [provider, setProvider] = useState("");
	S_CHANNELS_LIST_REQUEST_UPDATE.use([], () => void reloadLinked());

	const links = new Map<number, number>();
	for (const channel of Array.isArray(linked?.data) ? linked.data : [])
		if (channel.linked?.status !== "deleted") links.set(channel.id, (links.get(channel.id) ?? 0) + 1);
	const channels = (Array.isArray(all?.data) ? all.data : []).filter(channel => channelStatus(channel) !== "deleted");
	const providers = uniqueSorted(channels.map(channel => channel.provider?.name));
	const visible = channels
		.filter(channel => !provider || channel.provider?.name === provider)
		.filter(channel => matchesQuery(query, [channel.name, channel.id, channel.currency, channel.provider?.name, channel.terminal?.name]))
		.sort((a, b) => compareText(a.provider?.name, b.provider?.name) || compareText(a.name, b.name));
	const close = () => S_CHANNELS_LIST_POPUP.invoke(null);
	const link = (channel: TPaymentChannel) => S_MERCHANT_CHANNEL_EDIT.invoke({ channel, merchant_id: request.merchant_id, merchant_name: request.merchant_name });

	return (
		<ProviderDialog
			width={820}
			fullHeight
			eyebrow={request.merchant_name ? msg("templateWebsite_e4b54006", { v0: request.merchant_name }) : msg("templateMerchant_bf3dbf70", { v0: request.merchant_id })}
			title={msg("linkAChannel")}
			onClose={close}>
			<DialogBody>
				{all?.error && <Notice tone="error">{all.error.message}</Notice>}
				<div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
					<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameIdCurrency")} />
					{providers.length > 1 && (
						<FilterSelect label={msg("allProviders")} value={provider} onChange={setProvider} options={providers.map(name => ({ value: name, label: name }))} />
					)}
				</div>
				<Card>
					{!all ? (
						<SkeletonRows rows={4} />
					) : !visible.length ? (
						<EmptyState
							icon={<Cable size={20} />}
							title={channels.length ? msg("nothingMatchesTheSearch") : msg("noChannelsYet")}
							text={channels.length ? msg("tryAShorterQuery") : msg("createChannelsInProvidersChannelsFirst")}
						/>
					) : (
						<ItemList>
							{visible.map(channel => {
								const count = links.get(channel.id) ?? 0;
								return (
									<Item key={channel.id}>
										<MethodTile type={channel.terminal?.type} title={methodTypeLabel(channel.terminal?.type)} />
										<div className="item-main">
											<div style={{ color: "#182230", fontWeight: 600 }}>{channel.name}</div>
											<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px 8px", color: "#667085", fontSize: 12 }}>
												<span>{channel.provider?.name ?? "—"}</span>
												<span>· {channel.terminal?.name ?? methodTypeLabel(channel.terminal?.type)}</span>
												<Mono>#{channel.id}</Mono>
												{!channel.integration && <WarningText>{msg("noIntegration")}</WarningText>}
											</div>
										</div>
										<span className="item-side">
											<CardBrandIcons instrument={channel.instrument_type} />
											{channel.currency && <Chip>{channel.currency}</Chip>}
											{channelStatus(channel) !== "active" && <StatusPill status={channelStatus(channel)} />}
											{count > 0 && (
												<Pill tone="positive" dot>
													{count > 1 ? msg("templateLinked_b70ef84a", { v0: count }) : msg("linked")}
												</Pill>
											)}
											<AdminButton variant={count ? "secondarySmall" : "primarySmall"} size="small" onClick={() => link(channel)}>
												<Link2 size={14} />
												{count ? msg("linkAgain") : msg("link")}
											</AdminButton>
										</span>
									</Item>
								);
							})}
						</ItemList>
					)}
				</Card>
			</DialogBody>
		</ProviderDialog>
	);
};

export const ChannelRemoteItem = ({ type, id }: { type: TFeesTargetType; id: number }) => {
	useLocale();
	const requests: { [key in TFeesTargetType]: IAPIRequest | null } = {
		merchant: {
			method: "payments/channels/merchant/list",
			data: {
				id,
				type: type,
			},
		},
		client: null,
		provider: null,
	};

	const [channel, _reload, _busy] = useAPI<TPaymentChannel[]>(requests[type]);
	return <>{channel && channel.data && channel.data.length > 0 && <ChannelItem channel={channel.data[0]} preview />}</>;
};
